import * as echarts from 'echarts';

/**
 * The ONE shared ECharts theme (PLAN.md M7 "ECHARTS THEME"). Every chart registers + uses this so
 * the whole dashboard reads as one instrument panel: hairline axes (--line), muted gridlines,
 * tabular-num labels, transparent/paper background, NO default chrome.
 *
 * ECharts needs CONCRETE colors, not CSS variables, so we read the design tokens off the live
 * document at registration time (`resolveTokens()`), then build the theme from them. Re-register on
 * a theme toggle (light/dark) by calling `registerProjectionTheme()` again and re-initing charts.
 *
 * The fan-chart band tints, the categorical stress hues, and the semantic pos/neg/warn colors all
 * come from the SAME tokens the rest of the UI uses — chart and statement never drift.
 */

export const PROJECTION_THEME_NAME = 'projection';

export interface ProjectionTokens {
  bg: string;
  surface: string;
  surfaceSunken: string;
  line: string;
  ink: string;
  inkMuted: string;
  accent: string;
  accentWeak: string;
  accentFaint: string;
  pos: string;
  neg: string;
  warn: string;
  neutral: string;
  categorical: string[];
  fontUi: string;
  fontMono: string;
}

/** Read the resolved design-system tokens off `:root` (so charts match the active light/dark theme). */
export function resolveTokens(root: HTMLElement = document.documentElement): ProjectionTokens {
  const s = getComputedStyle(root);
  const v = (name: string): string => s.getPropertyValue(name).trim();
  return {
    bg: v('--bg'),
    surface: v('--surface'),
    surfaceSunken: v('--surface-sunken'),
    line: v('--line'),
    ink: v('--ink'),
    inkMuted: v('--ink-muted'),
    accent: v('--accent'),
    accentWeak: v('--accent-weak'),
    accentFaint: v('--accent-faint'),
    pos: v('--pos'),
    neg: v('--neg'),
    warn: v('--warn'),
    neutral: v('--neutral'),
    categorical: [v('--cat-1'), v('--cat-2'), v('--cat-3'), v('--cat-4'), v('--cat-5')],
    fontUi: v('--font-ui') || 'sans-serif',
    fontMono: v('--font-mono') || 'monospace',
  };
}

/**
 * Build the raw ECharts theme object from resolved tokens. Exposed so tests / Storybook can build
 * it without touching `echarts.registerTheme`. Strips default chrome: transparent background,
 * hairline split lines, muted axis text, tabular mono labels.
 */
export function buildProjectionTheme(t: ProjectionTokens): Record<string, unknown> {
  const axisLine = { lineStyle: { color: t.line, width: 1 } };
  const axisLabel = {
    color: t.inkMuted,
    fontFamily: t.fontMono,
    fontSize: 11,
  };
  const splitLine = { lineStyle: { color: t.line, width: 1, type: 'solid' } };
  return {
    color: t.categorical,
    backgroundColor: 'transparent',
    textStyle: { fontFamily: t.fontUi, color: t.ink },
    title: {
      textStyle: { fontFamily: t.fontUi, color: t.ink, fontWeight: 600 },
      subtextStyle: { color: t.inkMuted },
    },
    grid: { left: 8, right: 16, top: 16, bottom: 8, containLabel: true },
    categoryAxis: {
      axisLine,
      axisTick: { show: false },
      axisLabel,
      splitLine: { show: false },
    },
    valueAxis: {
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel,
      splitLine,
    },
    tooltip: {
      backgroundColor: t.surface,
      borderColor: t.line,
      borderWidth: 1,
      textStyle: { color: t.ink, fontFamily: t.fontUi, fontSize: 12 },
      extraCssText: 'box-shadow: var(--shadow-md); border-radius: 8px;',
      axisPointer: {
        type: 'line',
        lineStyle: { color: t.inkMuted, width: 1, type: 'dashed' },
        crossStyle: { color: t.inkMuted, width: 1 },
        label: { backgroundColor: t.ink },
      },
    },
    legend: {
      textStyle: { color: t.inkMuted, fontFamily: t.fontUi, fontSize: 12 },
      icon: 'roundRect',
      itemWidth: 10,
      itemHeight: 10,
    },
    line: { symbol: 'none', lineStyle: { width: 2 } },
    bar: { itemStyle: { borderRadius: [2, 2, 0, 0] } },
  };
}

let registeredFor: string | null = null;

/**
 * Register the shared theme under {@link PROJECTION_THEME_NAME}. Idempotent per active theme key
 * (light/dark): call again after a theme toggle to re-register from the new tokens. Returns the
 * theme name so a chart can `echarts.init(el, PROJECTION_THEME_NAME)`.
 */
export function registerProjectionTheme(root: HTMLElement = document.documentElement): string {
  const key = root.getAttribute('data-theme') ?? 'light';
  if (registeredFor === key) return PROJECTION_THEME_NAME;
  echarts.registerTheme(PROJECTION_THEME_NAME, buildProjectionTheme(resolveTokens(root)));
  registeredFor = key;
  return PROJECTION_THEME_NAME;
}

/** Force a re-register on the next call (used by the theme toggle). */
export function invalidateProjectionTheme(): void {
  registeredFor = null;
}
